/**
 * ModerationImageContent Component
 *
 * Displays multiple images in a responsive grid layout.
 * Supports 2-column grid for 2-4 images, 3-column for 5+.
 * Each image is clickable for fullscreen viewing in a lightbox.
 *
 * @example
 * ```tsx
 * <ModerationImageContent
 *   images={[
 *     { id: 'img1', url: 'https://...', alt: 'Screenshot 1' },
 *     { id: 'img2', url: 'https://...', alt: 'Screenshot 2' }
 *   ]}
 * />
 * ```
 */

import { Icon, IconSize, FullScreen, BaseItemWithImage, Close } from '@soteria/design-system-ve';
import { useState, useCallback, useEffect, type ReactNode } from 'react';

import * as styles from './ModerationCaseCard.css';

/**
 * Image item interface
 */
export interface ImageItem {
  /**
   * Unique identifier for the image
   */
  id: string;

  /**
   * Full-size image URL
   */
  url: string;

  /**
   * Alt text for accessibility
   */
  alt: string;

  /**
   * Optional thumbnail URL (uses full URL if not provided)
   */
  thumbnail?: string;
}

/**
 * Props for ModerationImageContent
 */
export interface ModerationImageContentProps {
  /**
   * Array of images to display
   */
  images: ImageItem[];

  /**
   * Optional className for custom styling
   */
  className?: string;
}

/**
 * ModerationImageContent Component
 *
 * Renders multiple images in a responsive grid with lightbox viewing.
 * Grid layout adjusts based on image count (2-column for 2-4, 3-column for 5+).
 */
export function ModerationImageContent({ images, className }: ModerationImageContentProps): ReactNode {
  const [selectedImage, setSelectedImage] = useState<ImageItem | null>(null);

  // Determine grid columns based on image count
  const gridColumns = images.length >= 5 ? 3 : images.length === 1 ? 1 : 2;

  // Handle image click
  const handleImageClick = useCallback((image: ImageItem) => {
    setSelectedImage(image);
  }, []);

  // Handle lightbox close
  const handleCloseLightbox = useCallback(() => {
    setSelectedImage(null);
  }, []);

  // Handle keyboard navigation in lightbox
  const handleKeyDown = useCallback(
    (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseLightbox();
      }
    },
    [handleCloseLightbox]
  );

  // Add/remove keyboard listener
  useEffect(() => {
    if (selectedImage) {
      window.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when lightbox is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [selectedImage, handleKeyDown]);

  if (!images || images.length === 0) {
    return null;
  }

  const gridClassName = [styles.imageGrid({ columns: gridColumns as 1 | 2 | 3 }), className].filter(Boolean).join(' ');

  return (
    <>
      <div className={gridClassName}>
        {images.map((image, index) => (
          <BaseItemWithImage
            key={image.id}
            aria-label={`View ${image.alt || `image ${index + 1}`} fullscreen`}
            as="button"
            className={styles.imageContainer}
            onPress={() => handleImageClick(image)}
          >
            <BaseItemWithImage.Image>
              <img alt={image.alt || `Image ${index + 1}`} className={styles.imageThumbnail} loading="lazy" src={image.thumbnail || image.url} />
              <button
                aria-label="Expand image"
                className={styles.expandButton}
                tabIndex={-1}
                onClick={e => {
                  e.stopPropagation();
                  handleImageClick(image);
                }}
              >
                <Icon icon={FullScreen} iconSize={IconSize.Small} />
              </button>
            </BaseItemWithImage.Image>
          </BaseItemWithImage>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */
        <div
          aria-label="Image lightbox"
          aria-modal="true"
          className={styles.lightboxOverlay}
          role="dialog"
          tabIndex={-1}
          onClick={handleCloseLightbox}
          onKeyDown={e => {
            if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
              handleCloseLightbox();
            }
          }}
        >
          <div className={styles.lightboxContent} role="document" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
            <img alt={selectedImage.alt} className={styles.lightboxImage} src={selectedImage.url} />
            <button aria-label="Close lightbox" className={styles.lightboxClose} type="button" onClick={handleCloseLightbox}>
              <Icon icon={Close} iconSize={IconSize.Small} />
            </button>
          </div>
        </div>
        /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */
      )}
    </>
  );
}

ModerationImageContent.displayName = 'ModerationImageContent';
